import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  IconButton,
  Button,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Divider
} from '@mui/material';
import { ReplyOutlinedIcon, ArrowBackIosNewOutlinedIcon, KeyboardArrowDownOutlinedIcon, MoreHorizOutlinedIcon, VisibilityOutlinedIcon, ShareOutlinedIcon, ContentCopyOutlinedIcon, QrCodeOutlinedIcon, LockResetOutlinedIcon, DeleteOutlineIcon } from 'icons/materialIcons';
import { getUserById } from 'apis/authApi';
import { getCardById, duplicateCard, deleteCard } from 'apis/cardApi';
import DarkButtonWithIcon from 'components/common/button/DarkButtonWithIcon';
import NewCardModal from 'components/editCard/editCardTopNavbar/NewCardModal';
import CreateCardIndividualOrTeamMemberModal from 'components/editCard/editCardTopNavbar/CreateCardIndividualOrTeamMemberModal';
import UpgradeSnackBar from 'components/common/notification/UpgradeSnackBar';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import userAvatar from 'images/avatar-cropped.png';
import { v4 as uuidv4 } from 'uuid'; // Import UUID library
import ShareYourCardModal from 'components/common/ShareYourCardModal';
import QRCodeStyling from 'qr-code-styling';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import { useQRCodeContext } from 'contexts/QrCodeContext'; // Import QRCode context
import { useAuth } from 'contexts/AuthContext';
import { forgotPassword } from 'apis/authApi';
import { useCardContext } from 'contexts/CardContext';
import DeleteCardModal from 'components/editCard/editCardTopNavbar/DeleteCardModal';

const EditCardNavbar = ({ user }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [userName, setUserName] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const { cardId } = useParams();
  const [cardDetails, setCardDetails] = useState({});
  const [cardName, setCardName] = useState('');
  const [cardFullName, setCardFullName] = useState('');
  const [cardProfilePictureUrl, setCardProfilePictureUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [cardEmail, setCardEmail] = useState('');
  const [isNewCardModalOpen, setIsNewCardModalOpen] = useState(false);
  const [isCreateCardTypeModalOpen, setIsCreateCardTypeModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const buttonRef = useRef(null);
  const tempCardId = uuidv4();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const qrDownloadRef = useRef(null);
  const { authState } = useAuth();
  const { updateCardData } = useCardContext();

  // Menu state for three dots button
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  const [isDeleteCardModalOpen, setIsDeleteCardModalOpen] = useState(false);

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Get QR Code context
  const { qrCodeState, initializeQRCode } = useQRCodeContext();

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleNotificationClose = () => {
    setNotification({ ...notification, open: false });
  };

  const handleShareCard = () => {
    setShareModalOpen(true);
    handleMenuClose();
  };

  const handleViewCard = () => {
    // Open the card in a new tab
    window.open(`/card/${cardId}`, '_blank');
    handleMenuClose();
  };

  const handleDuplicateCard = async () => {
    try {
      setNotification({
        open: true,
        message: 'Duplicating card...',
        severity: 'info'
      });
      handleMenuClose();

      const response = await duplicateCard(cardId, user.userId);

      if (response.success) {
        setNotification({
          open: true,
          message: 'Card duplicated successfully',
          severity: 'success'
        });

        // Navigate to the dashboard
        // Navigate with state containing the notification
        navigate('/dashboard/mycards', {
          state: {
            notification: {
              message: 'Card duplicated successfully',
              severity: 'success'
            }
          }
        });
      }
    } catch (error) {
      console.error('Error duplicating card:', error);
      setNotification({
        open: true,
        message: error.response?.data?.message || 'Failed to duplicate card',
        severity: 'error'
      });
    }
  };



  // REMOVE this existing function:
  const handleDeleteCard = async () => {
    try {
      // Show confirmation dialog
      if (!window.confirm('Are you sure you want to delete this card? This action cannot be undone.')) {
        return;
      }

      setNotification({
        open: true,
        message: 'Deleting card...',
        severity: 'info'
      });
      handleMenuClose();

      const response = await deleteCard(cardId, user.userId);

      if (response.success) {
        setNotification({
          open: true,
          message: 'Card deleted successfully',
          severity: 'success'
        });

        // Navigate back to the cards container
        navigate('/dashboard/mycards');
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to delete card',
        severity: 'error'
      });
    }
  };

  // REPLACE with these three new functions:
  const handleDeleteCardClick = () => {
    handleMenuClose();
    setIsDeleteCardModalOpen(true);
  };

  const handleDeleteCardConfirm = async () => {
    try {
      setNotification({
        open: true,
        message: 'Deleting card...',
        severity: 'info'
      });
      setIsDeleteCardModalOpen(false);

      const response = await deleteCard(cardId, user.userId);

      if (response.success) {
        setNotification({
          open: true,
          message: 'Card deleted successfully',
          severity: 'success'
        });

        // Navigate back to the cards container
        navigate('/dashboard/mycards');
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to delete card',
        severity: 'error'
      });
    }
  };

  const handleDeleteCardCancel = () => {
    setIsDeleteCardModalOpen(false);
  };



  // Initialize QR Code for download
  useEffect(() => {
    if (cardId) {
      initializeQRCode(cardId);

      // Generate the card URL for QR code
      const BASE_URL = `${FRONTEND_BASE_URL}/card`;
      const cardUrl = `${BASE_URL}/${cardId}?source=qr_code&ts=${Date.now()}`;

      // Initialize the QR code generator for downloading
      const contextQrCodeColor = qrCodeState[cardId]?.contextQrCodeColor || '#000000';
      const contextQrCodeLogoUrl = qrCodeState[cardId]?.contextQrCodeLogoUrl || '';

      qrDownloadRef.current = new QRCodeStyling({
        width: 1000,
        height: 1000,
        type: "png",
        data: cardUrl,
        image: contextQrCodeLogoUrl,
        dotsOptions: {
          color: contextQrCodeColor,
          type: "square"
        },
        cornersSquareOptions: {
          type: "square"
        },
        cornersDotOptions: {
          type: "square"
        },
        backgroundOptions: {
          color: "#ffffff"
        },
        imageOptions: {
          crossOrigin: "anonymous",
          margin: 7,
          imageSize: 0.4,
          hideBackgroundDots: true,
        },
        qrOptions: {
          errorCorrectionLevel: "H"
        },
      });
    }
  }, [cardId, initializeQRCode, qrCodeState]);

  const handleDownloadQRCode = async () => {
    if (!qrDownloadRef.current || downloading) return;

    try {
      setDownloading(true);
      handleMenuClose();

      const fileName = `qr-code-${cardId || 'card'}`;
      await qrDownloadRef.current.download({
        extension: 'png',
        name: fileName,
      });
    } catch (error) {
      console.error('Error downloading QR code:', error);
      setNotification({
        open: true,
        message: 'Error downloading QR code. Please try again.',
        severity: 'error'
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleResetPassword = async () => {
    // Use the email from card or from the logged-in user
    const userEmail = cardEmail || authState?.user?.email;

    if (!userEmail) {
      setNotification({
        open: true,
        message: 'Unable to determine email address for password reset.',
        severity: 'error'
      });
      handleMenuClose();
      return;
    }

    try {
      setResettingPassword(true);
      handleMenuClose();

      const response = await forgotPassword(userEmail);

      if (response.success) {
        setNotification({
          open: true,
          message: 'Password reset email sent successfully.',
          severity: 'success'
        });
      } else {
        throw new Error(response.message || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Error sending password reset:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to send password reset email.',
        severity: 'error'
      });
    } finally {
      setResettingPassword(false);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  useEffect(() => {
    const fetchCardDetails = async () => {
      try {
        console.log('Fetching card details...');
        const data = await getCardById(cardId);
        setCardDetails(data);
        setLoading(false);
        console.log('Card Details:', data);
      } catch (error) {
        console.error('Error fetching card details:', error);
        setLoading(false);
      }
    };

    // ✅ FIX: Skip backend call for new card creation (UUID)
    const isNewCardCreation = cardId && cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

    if (cardId && !isNewCardCreation) {
      fetchCardDetails();
    } else if (isNewCardCreation) {
      console.log('🔍 EditCardNavbar: New card creation - skipping card details fetch');
      setLoading(false);
    }
  }, [cardId]);

  useEffect(() => {
    if (cardDetails?.card) {
      setCardFullName(cardDetails.card.contact_info?.fullName || '');
      setCardProfilePictureUrl(cardDetails.card.profilePictureUrl || '');
      setCardName(cardDetails.card.cardName || '');
      setCardEmail(cardDetails.card.contact_info?.email || '');
    }
  }, [cardDetails]);

  useEffect(() => {
    console.log("card profile photo++", cardProfilePictureUrl);
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const idToken = localStorage.getItem('authToken');
        if (!idToken) {
          throw new Error('No auth token found');
        }
        const response = await getUserById(user.userId, idToken);
        setUserData(response.data);
        console.log('Fetched user data from EditCardNavbar:', response.data.fullName);
      } catch (error) {
        console.error('Error fetching user data:', error.message);
      }
    };

    fetchUserData();
  }, [user.userId]);

  // Modified handleCreateNewCard function
  const handleCreateNewCard = () => {
    // Original logic - commented out but kept for reference
    // setIsCreateCardTypeModalOpen(true);

    // New logic - directly navigate to create card for myself
    navigate(`/dashboard/mycards/add-card/${tempCardId}?action=create`);
    window.location.reload();
  };

  const handleCardTypeSelect = (type) => {
    if (type === 'team-member') {
      setSnackbarOpen(true);
    } else if (type === 'myself') {
      navigate(`/dashboard/mycards/add-card/${tempCardId}?action=create`);
      window.location.reload();
    }
    setIsCreateCardTypeModalOpen(false);
    setIsNewCardModalOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSubscribe = () => {
    setSnackbarOpen(false);
    navigate('/dashboard/subscribe');
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm="auto">
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <IconButton
                sx={{
                  backgroundColor: 'transparent',
                  color: '#828282',
                  padding: 0,
                }}
                onClick={handleBackClick}
              >
                <ArrowBackIosNewOutlinedIcon />
              </IconButton>
            </Grid>
            <Grid item>
              <Box
                sx={{
                  height: 25,
                  width: '1px',
                  backgroundColor: '#e0e0e0',
                }}
              />
            </Grid>
            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  src={cardProfilePictureUrl || userAvatar}
                  alt="User Avatar"
                  sx={{
                    width: 50,
                    height: 50,
                    border: '2px solid #ffffff',
                  }}
                />
                <Box sx={{ marginLeft: 2 }}>
                  <Typography variant="body2" sx={{ fontSize: '1.2rem', fontWeight: 600, color: '#333' }}>
                    {cardFullName}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#828282', fontSize: '0.9rem', fontWeight: 500 }}>
                    {cardEmail}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm container justifyContent="flex-end" spacing={2}>
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                ref={buttonRef}
                variant="contained"
                sx={{
                  backgroundColor: '#fff',
                  color: '#e0e0e0',
                  fontWeight: '500',
                  borderRadius: '8px',
                  border: '1px #e0e0e0 solid',
                  textTransform: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  alignSelf: 'center',
                  width: '200px',
                  padding: '6.5px 16px',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#fff',
                    color: '#e0e0e0',
                    boxShadow: 'none',
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: 1,
                  },
                  '& .MuiButton-endIcon': {
                    marginLeft: 1,
                  },
                }}
                startIcon={
                  <Avatar
                    src={cardProfilePictureUrl || userAvatar}
                    alt="User Avatar"
                    sx={{
                      width: 25,
                      height: 25,
                      border: '2px solid #ffffff',
                    }}
                  />
                }
                endIcon={<KeyboardArrowDownOutlinedIcon />}
                onClick={() => {
                  // Keep original behavior to open the first modal
                  setIsNewCardModalOpen(true);
                }}
              >
                <Typography sx={{ color: '#000', flexGrow: 1, textAlign: 'left', fontSize: "0.9rem", marginLeft: "10px" }}>
                  {'New Card' || cardName}
                </Typography>
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DarkButtonWithIcon
                text="Share Your Card"
                icon={<ReplyOutlinedIcon />}
                iconPosition="end"
                onClick={handleShareCard}
              />

              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#fff',
                  color: '#cccccc',
                  fontWeight: '500',
                  borderRadius: '8px',
                  border: '1px #e0e0e0 solid',
                  textTransform: 'none',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '75px',
                  padding: '6.5px 16px',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#fff',
                    color: '#828282',
                    boxShadow: 'none',
                  },
                }}
                onClick={handleMenuOpen}
              >
                <MoreHorizOutlinedIcon sx={{ color: '#828282' }} />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {/* Keep these modal components in the codebase, but they won't be used in the updated flow */}
      <NewCardModal
        isOpen={isNewCardModalOpen}
        onClose={() => setIsNewCardModalOpen(false)}
        onCreate={handleCreateNewCard}
        anchorEl={buttonRef.current}
      />

      <CreateCardIndividualOrTeamMemberModal
        isOpen={isCreateCardTypeModalOpen}
        onClose={() => setIsCreateCardTypeModalOpen(false)}
        onSelect={handleCardTypeSelect}
        anchorEl={buttonRef.current}
      />

      <UpgradeSnackBar
        open={snackbarOpen}
        handleClose={handleSnackbarClose}
        handleSubscribe={handleSubscribe}
      />

      {/* Share Card Modal */}
      <ShareYourCardModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        cardId={cardId} // Pass the specific card ID
      />

      {/* Three dots menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '200px'
          }
        }}
      >
        <MenuItem onClick={handleViewCard}>
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View Card" />
        </MenuItem>
        <MenuItem onClick={handleShareCard}>
          <ListItemIcon>
            <ShareOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Share Card" />
        </MenuItem>
        <MenuItem onClick={handleDuplicateCard}>
          <ListItemIcon>
            <ContentCopyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Duplicate Card" />
        </MenuItem>
        <MenuItem onClick={handleDownloadQRCode}>
          <ListItemIcon>
            <QrCodeOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Download QR Code" />
        </MenuItem>
        <MenuItem onClick={handleResetPassword}>
          <ListItemIcon>
            <LockResetOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Reset Password" />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleDeleteCardClick}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText primary="Delete Card" />
        </MenuItem>
      </Menu>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        handleClose={handleNotificationClose}
        severity={notification.severity}
      />

      {/* Delete Card Modal */}
      <DeleteCardModal
        open={isDeleteCardModalOpen}
        onClose={handleDeleteCardCancel}
        onConfirm={handleDeleteCardConfirm}
      />


    </Box>
  );
};

export default EditCardNavbar;